<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

// saveHandler is a prop rather than an emit so the dialog can wait for the
// round trip and keep the operator's input when Meta rejects the write.
const props = defineProps({
  saveHandler: { type: Function, required: true },
});

const emit = defineEmits(['delete']);

const COMPONENT_TYPES = [
  'carousel_quick_reply',
  'carousel_url',
  'cta_url',
  'flow',
  'image',
  'interactive_list',
  'location',
  'location_request',
];
// Undocumented, but the live API rejects anything else — Meta's own example
// title ("Product carousel") is refused.
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const { t } = useI18n();

const uiSkills = useMapGetter('metaAgent/getUiSkills');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

const dialogRef = ref(null);
const editingId = ref(null);
const title = ref('');
const componentType = ref('cta_url');
const status = ref('enabled');
const instruction = ref('');
const flowId = ref('');

const isFlow = computed(() => componentType.value === 'flow');
const isTitleValid = computed(() => SLUG.test(title.value));
const canSave = computed(
  () =>
    isTitleValid.value &&
    instruction.value.trim().length > 0 &&
    (!isFlow.value || flowId.value)
);

const openDialog = uiSkill => {
  editingId.value = uiSkill?.id || null;
  title.value = uiSkill?.title || '';
  componentType.value = uiSkill?.component_type || 'cta_url';
  status.value = uiSkill?.status || 'enabled';
  instruction.value = uiSkill?.instruction || '';
  flowId.value = uiSkill?.flow_id || '';
  dialogRef.value.open();
};

const dialogTitle = computed(() =>
  editingId.value
    ? t('META_AGENT.SKILLS.RICH_MESSAGES.EDIT_TITLE')
    : t('META_AGENT.SKILLS.RICH_MESSAGES.ADD')
);

const save = async () => {
  const uiSkill = {
    title: title.value,
    component_type: componentType.value,
    status: status.value,
    instruction: instruction.value,
  };
  if (isFlow.value) uiSkill.flow_id = flowId.value;
  const saved = await props.saveHandler({
    uiSkillId: editingId.value,
    uiSkill,
  });
  if (saved) dialogRef.value.close();
};

const remove = uiSkill => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('META_AGENT.SKILLS.RICH_MESSAGES.DELETE_CONFIRM')))
    return;
  emit('delete', uiSkill.id);
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start justify-between gap-4">
      <span class="text-sm text-n-slate-11">
        {{ t('META_AGENT.SKILLS.RICH_MESSAGES.DESCRIPTION') }}
      </span>
      <Button
        :label="t('META_AGENT.SKILLS.RICH_MESSAGES.ADD')"
        size="sm"
        icon="i-lucide-plus"
        @click="openDialog(null)"
      />
    </div>

    <div
      v-for="uiSkill in uiSkills"
      :key="uiSkill.id"
      class="flex items-start justify-between gap-4 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
    >
      <div class="flex flex-col gap-1 min-w-0">
        <div class="flex items-center gap-2">
          <span class="font-mono text-sm text-n-slate-12">
            {{ uiSkill.title }}
          </span>
          <span
            class="px-2 py-0.5 text-xs rounded-md"
            :class="
              uiSkill.status === 'enabled'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            {{
              t(`META_AGENT.SKILLS.RICH_MESSAGES.STATUSES.${uiSkill.status}`)
            }}
          </span>
        </div>
        <span class="text-sm text-n-slate-11">
          {{
            t(
              `META_AGENT.SKILLS.RICH_MESSAGES.COMPONENTS.${uiSkill.component_type}`
            )
          }}
        </span>
        <span class="text-xs truncate text-n-slate-10">
          {{ uiSkill.instruction }}
        </span>
      </div>
      <div class="flex items-center flex-shrink-0 gap-1">
        <Button
          :label="t('META_AGENT.SKILLS.RICH_MESSAGES.EDIT')"
          size="xs"
          variant="faded"
          color="slate"
          @click="openDialog(uiSkill)"
        />
        <Button
          :label="t('META_AGENT.SKILLS.RICH_MESSAGES.DELETE')"
          size="xs"
          variant="ghost"
          color="ruby"
          @click="remove(uiSkill)"
        />
      </div>
    </div>

    <span v-if="!uiSkills.length" class="text-sm text-n-slate-10">
      {{ t('META_AGENT.SKILLS.RICH_MESSAGES.EMPTY') }}
    </span>

    <Dialog
      ref="dialogRef"
      :title="dialogTitle"
      :confirm-button-label="t('META_AGENT.SKILLS.RICH_MESSAGES.SAVE')"
      :disable-confirm-button="!canSave"
      :is-loading="uiFlags.savingSkill"
      width="xl"
      @confirm="save"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="title"
          :label="t('META_AGENT.SKILLS.RICH_MESSAGES.TITLE')"
          :placeholder="t('META_AGENT.SKILLS.RICH_MESSAGES.TITLE_PLACEHOLDER')"
          :message="t('META_AGENT.SKILLS.RICH_MESSAGES.TITLE_HINT')"
          :message-type="title && !isTitleValid ? 'error' : 'info'"
        />
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('META_AGENT.SKILLS.RICH_MESSAGES.COMPONENT_TYPE') }}
          </label>
          <select v-model="componentType" class="!mb-0">
            <option v-for="type in COMPONENT_TYPES" :key="type" :value="type">
              {{ t(`META_AGENT.SKILLS.RICH_MESSAGES.COMPONENTS.${type}`) }}
            </option>
          </select>
        </div>
        <Input
          v-if="isFlow"
          v-model="flowId"
          :label="t('META_AGENT.SKILLS.RICH_MESSAGES.FLOW_ID')"
        />
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('META_AGENT.SKILLS.RICH_MESSAGES.STATUS') }}
          </label>
          <select v-model="status" class="!mb-0">
            <option value="enabled">
              {{ t('META_AGENT.SKILLS.RICH_MESSAGES.STATUSES.enabled') }}
            </option>
            <option value="disabled">
              {{ t('META_AGENT.SKILLS.RICH_MESSAGES.STATUSES.disabled') }}
            </option>
          </select>
        </div>
        <TextArea
          v-model="instruction"
          :label="t('META_AGENT.SKILLS.RICH_MESSAGES.INSTRUCTION')"
          :placeholder="
            t('META_AGENT.SKILLS.RICH_MESSAGES.INSTRUCTION_PLACEHOLDER')
          "
          :max-length="1024"
          auto-height
        />
      </div>
    </Dialog>
  </div>
</template>
